import { useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Pencil,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { PageTitle } from "components/page-title";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionTooltipButton } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { Alert, AlertDescription } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Field, FieldError, FieldLabel } from "components/ui/field";
import { Input } from "components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Skeleton } from "components/ui/skeleton";
import { Spinner } from "components/ui/spinner";
import { Textarea } from "components/ui/textarea";
import { updateAppValidationSchema } from "components/api-keys/validation";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { ApiKey, ModifyApiKey } from "data/models";
import { deleteApiKey, editApiKey } from "data/mutations-common";
import { fetchApiKeys, fetchApiProducts } from "data/queries-common";
import * as Routes from "routes";
import { formatTimestamp } from "utils/format-date";
import { resolveIdentityId } from "utils/identity";
import { getApiProductOptions } from "./api-product-options";
import {
  ApiAccessSelection,
  AppFormActionBar,
  FocusValidationErrors,
  UnsavedChangesWarning,
} from "./app-form-sections";

function CredentialField({
  id,
  label,
  value,
}: {
  id: string;
  label: string;
  value: string;
}) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  return (
    <Field>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          readOnly
          className="min-w-0 font-mono text-xs"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={`${visible ? "Hide" : "Show"} ${label.toLowerCase()}`}
          aria-pressed={visible}
          onClick={() => setVisible(!visible)}
        >
          {visible ? <EyeOff /> : <Eye />}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={`Copy ${label.toLowerCase()}`}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              toast.success(`${label} copied`);
            } catch {
              toast.error(
                `Unable to copy ${label.toLowerCase()}. Reveal it and copy it manually.`,
              );
            }
          }}
        >
          {copied ? <Check /> : <Copy />}
        </Button>
      </div>
    </Field>
  );
}

export const EditAppPage = () => {
  const history = useHistory();
  const location = useLocation<{ scrollToCredentials?: boolean }>();
  const { keyName } = useParams<{ keyName: string }>();
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const queryClient = useQueryClient();
  const { isPermitted } = usePermissions();
  const canEdit = isPermitted([PermissionsKeys.appsUpdate]);
  const saved = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);

  const apiKeysQuery = useQuery({
    queryKey: [QueryKeys.apiKeys, identityId],
    queryFn: fetchApiKeys,
  });
  const apiProductsQuery = useQuery({
    queryKey: [QueryKeys.apiProducts],
    queryFn: fetchApiProducts,
  });
  const app = apiKeysQuery.data?.find((key) => key.name === keyName);
  const isFormReady = Boolean(app && apiProductsQuery.data);

  useEffect(() => {
    if (!location.state?.scrollToCredentials || !isFormReady) return;
    const credentials =
      formRef.current?.querySelector<HTMLElement>("#credentials");
    const content = credentials?.closest("main");
    if (!credentials || !content) return;

    // Scroll only the content so that the app header stays in place.
    content.scrollTo({
      top:
        content.scrollTop +
        credentials.getBoundingClientRect().top -
        content.getBoundingClientRect().top -
        16,
      behavior: "instant",
    });
    credentials.focus({ preventScroll: true });
  }, [location.state?.scrollToCredentials, keyName, isFormReady]);

  const updateMutation = useMutation({
    mutationFn: (values: ModifyApiKey) =>
      editApiKey({
        ...values,
        status:
          values.status === "approved"
            ? "approve"
            : values.status === "revoked"
              ? "revoke"
              : values.status,
      }),
    onSuccess: (updatedApp: ApiKey) => {
      queryClient.setQueryData<ApiKey[]>(
        [QueryKeys.apiKeys, identityId],
        (apps) =>
          apps?.map((key) => (key.name === updatedApp.name ? updatedApp : key)),
      );
      toast.success("App changes saved");
    },
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteApiKey(keyName),
    onSuccess: (deletedApp: ApiKey) => {
      queryClient.setQueryData<ApiKey[]>(
        [QueryKeys.apiKeys, identityId],
        (apps) => apps?.filter((key) => key.name !== deletedApp.name),
      );
      saved.current = true;
      toast.success("App deleted");
      history.push(Routes.developerApiKeys);
    },
  });
  const isBusy = updateMutation.isPending || deleteMutation.isPending;
  const isLoading = apiKeysQuery.isPending || apiProductsQuery.isPending;
  const availableProducts = [...(apiProductsQuery.data ?? [])];
  // Keep existing API access visible when a product is absent from the public catalogue.
  for (const product of app?.products ?? []) {
    if (
      !availableProducts.some(
        (available) => available.name === product.productName,
      )
    ) {
      availableProducts.push({
        name: product.productName,
        displayName: product.productDisplayName,
      });
    }
  }
  const options = getApiProductOptions(availableProducts);

  return (
    <div className="max-w-3xl space-y-6">
      <PageTitle
        icon={Pencil}
        title="Edit App"
        description="Manage your app details, API access and credentials."
        actions={
          <Button
            variant="ghost"
            size="sm"
            disabled={isBusy}
            onClick={() => history.push(Routes.developerApiKeys)}
          >
            <ArrowLeft />
            Back to My Apps
          </Button>
        }
      />
      <ReactQueryErrorWrapper queries={[apiKeysQuery, apiProductsQuery]} />
      {isLoading && (
        <div className="space-y-6" aria-label="Loading app">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      )}
      {(apiKeysQuery.isError || apiProductsQuery.isError) && (
        <Button
          variant="outline"
          onClick={() => {
            if (apiKeysQuery.isError) apiKeysQuery.refetch();
            if (apiProductsQuery.isError) apiProductsQuery.refetch();
          }}
        >
          Try again
        </Button>
      )}
      {apiKeysQuery.isSuccess && !app && (
        <Alert>
          <AlertDescription>
            This app could not be found. Return to My Apps to choose another
            app.
          </AlertDescription>
        </Alert>
      )}
      {app && apiProductsQuery.data && (
        <Formik<ModifyApiKey>
          key={`${identityId}-${keyName}`}
          initialValues={{
            name: app.name,
            displayName: app.displayName || app.name,
            notes: app.notes ?? "",
            status: app.status,
            products: app.products.map((product) => product.productName),
          }}
          validationSchema={updateAppValidationSchema}
          onSubmit={(values, { resetForm, setSubmitting }) => {
            if (!canEdit || isBusy) {
              setSubmitting(false);
              return;
            }
            updateMutation.mutate(
              { ...values, displayName: values.displayName.trim() },
              {
                onSuccess: (updatedApp: ApiKey) =>
                  resetForm({
                    values: {
                      name: updatedApp.name,
                      displayName: updatedApp.displayName || updatedApp.name,
                      notes: updatedApp.notes ?? "",
                      status: updatedApp.status,
                      products: updatedApp.products.map(
                        (product) => product.productName,
                      ),
                    },
                  }),
                onSettled: () => setSubmitting(false),
              },
            );
          }}
        >
          {({
            values,
            handleChange,
            handleBlur,
            errors,
            touched,
            dirty,
            setFieldValue,
          }) => (
            <Form ref={formRef} className="space-y-6" noValidate>
              <UnsavedChangesWarning
                saved={saved}
                isSaving={isBusy}
                message="You have unsaved app changes. If you leave, your changes will be lost."
              />
              <FocusValidationErrors formRef={formRef} />
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                  <CardDescription>
                    Give your app a name and description to help identify it.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Field>
                    <FieldLabel htmlFor="displayName">
                      App Name <span className="text-destructive">*</span>
                    </FieldLabel>
                    <Input
                      id="displayName"
                      name="displayName"
                      value={values.displayName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. My Address Lookup Service"
                      maxLength={60}
                      required
                      disabled={!canEdit || isBusy}
                      aria-invalid={touched.displayName && !!errors.displayName}
                      aria-describedby={
                        touched.displayName && errors.displayName
                          ? "name-error"
                          : undefined
                      }
                    />
                    {touched.displayName && errors.displayName && (
                      <FieldError id="name-error">
                        {errors.displayName}
                      </FieldError>
                    )}
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="notes">Description</FieldLabel>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={values.notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="What will this app be used for?"
                      maxLength={200}
                      className="field-sizing-fixed min-h-20"
                      disabled={!canEdit || isBusy}
                      aria-invalid={touched.notes && !!errors.notes}
                      aria-describedby="description-help"
                    />
                    <p
                      id="description-help"
                      className="text-xs text-muted-foreground"
                    >
                      Optional. Helps you remember the purpose of this app.
                    </p>
                    {touched.notes && errors.notes && (
                      <FieldError>{errors.notes}</FieldError>
                    )}
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="app-status">App status</FieldLabel>
                    <Select
                      value={values.status}
                      onValueChange={(status) =>
                        setFieldValue("status", status)
                      }
                      disabled={!canEdit || isBusy}
                    >
                      <SelectTrigger
                        id="app-status"
                        className="w-full"
                        aria-invalid={touched.status && !!errors.status}
                        aria-describedby={
                          touched.status && errors.status
                            ? "status-error"
                            : undefined
                        }
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="revoked">Revoked</SelectItem>
                        {!["approved", "revoked"].includes(values.status) && (
                          <SelectItem value={values.status} disabled>
                            {values.status}
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {touched.status && errors.status && (
                      <FieldError id="status-error">{errors.status}</FieldError>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Revoking the app stops API requests using its credential.
                      Status changes take effect when you save.
                    </p>
                  </Field>
                  {values.status !== app.status &&
                    values.status === "revoked" && (
                      <Alert variant="warning">
                        <AlertDescription>
                          Saving will revoke this app. Integrations using it
                          will lose access.
                        </AlertDescription>
                      </Alert>
                    )}
                </CardContent>
              </Card>
              <ApiAccessSelection
                options={options}
                isSaving={isBusy}
                readOnly={!canEdit}
                allowExistingUnavailable
              />
              <Card
                id="credentials"
                tabIndex={-1}
                role="region"
                aria-label="Credentials"
                className="outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <KeyRound className="size-4" />
                    Credentials
                  </CardTitle>
                  <CardDescription>
                    Use these credentials to connect your integration. Keep them
                    private.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <CredentialField
                    key={`${app.name}-key`}
                    id="consumer-key"
                    label="API Key"
                    value={app.consumerKey}
                  />
                  <CredentialField
                    key={`${app.name}-secret`}
                    id="consumer-secret"
                    label="Consumer Secret"
                    value={app.consumerSecret}
                  />
                  <dl className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                    <div>
                      <dt className="text-xs text-muted-foreground">Created</dt>
                      <dd className="mt-1 text-sm">
                        {formatTimestamp(app.createdDate)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Expires</dt>
                      <dd className="mt-1 text-sm">
                        {formatTimestamp(app.expireDate)}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>
              <ReactQueryErrorWrapper mutations={[updateMutation]} />
              <Card className="border-destructive/30">
                <CardHeader>
                  <CardTitle>Delete App</CardTitle>
                  <CardDescription>
                    Permanently remove this app and its credential. Integrations
                    using it will lose access.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <PermissionTooltipButton
                    type="button"
                    permissionKeys={[PermissionsKeys.appsDelete]}
                    variant="destructive"
                    additionalDisabled={isBusy}
                    onClick={() => {
                      deleteMutation.reset();
                      setDeleteModalIsOpen(true);
                    }}
                  >
                    <Trash2 />
                    Delete app
                  </PermissionTooltipButton>
                </CardContent>
              </Card>
              <AppFormActionBar
                isSaving={isBusy}
                pendingStatus={
                  deleteMutation.isPending ? "Deleting app…" : "Saving changes…"
                }
                initialStatus="No unsaved changes"
              >
                <Button
                  type="button"
                  variant="outline"
                  disabled={isBusy}
                  onClick={() => history.push(Routes.developerApiKeys)}
                >
                  Cancel
                </Button>
                <PermissionTooltipButton
                  type="submit"
                  permissionKeys={[PermissionsKeys.appsUpdate]}
                  additionalDisabled={!dirty || isBusy}
                >
                  {updateMutation.isPending ? <Spinner /> : <Save />}
                  {updateMutation.isPending ? "Saving…" : "Save changes"}
                </PermissionTooltipButton>
              </AppFormActionBar>
            </Form>
          )}
        </Formik>
      )}
      <ConfirmationModal
        open={deleteModalIsOpen}
        isLoading={deleteMutation.isPending}
        isError={deleteMutation.isError}
        header="Delete this app?"
        body={`Deleting “${app?.displayName || app?.name}” permanently removes the app and its credential. Integrations using it will lose access. This cannot be undone.`}
        leftButtonText="Cancel"
        leftButtonColor="primary"
        rightButtonText="Delete app"
        rightButtonColor="error"
        handleLeftButton={() => setDeleteModalIsOpen(false)}
        handleRightButton={() => deleteMutation.mutate()}
        handleClose={() => {
          if (!deleteMutation.isPending) setDeleteModalIsOpen(false);
        }}
      />
    </div>
  );
};
